import { useState } from "react";
import { Copy, Share, WhatsappLogo } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useToast } from "./Toast";
import styles from "./ShareRow.module.css";

type Props = {
  url: string;
  title: string;
  /** Optional: Name des Events fuer den Share-Text. */
  eventName?: string;
};

export function ShareRow({ url, title, eventName }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [copyState, setCopyState] = useState<"idle" | "done">("idle");

  const shareText = eventName
    ? t("admin.shareMessage", { name: eventName, url })
    : url;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState("done");
      toast.success(t("common.copied"));
      window.setTimeout(() => setCopyState("idle"), 1500);
    } catch {
      toast.error(t("common.errorGeneric"));
    }
  };

  const nativeShare = async () => {
    if (typeof navigator.share !== "function") {
      await copy();
      return;
    }
    try {
      await navigator.share({ title, text: shareText, url });
    } catch {
      // User hat abgebrochen — stille behandeln.
    }
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const hasNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <div className={styles.row}>
      <button
        type="button"
        className={styles.button}
        onClick={copy}
        aria-label={t("common.copy")}
      >
        <Copy size={16} weight="regular" />
        <span>{copyState === "done" ? t("common.copied") : t("common.copy")}</span>
      </button>
      <a
        className={styles.button}
        href={whatsappUrl}
        target="_blank"
        rel="noreferrer"
      >
        <WhatsappLogo size={16} weight="regular" />
        <span>{t("admin.shareWhatsapp")}</span>
      </a>
      {hasNativeShare && (
        <button type="button" className={styles.button} onClick={nativeShare}>
          <Share size={16} weight="regular" />
          <span>{t("admin.shareMore")}</span>
        </button>
      )}
    </div>
  );
}
